import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchWorkspaceEntitlements,
  updateWorkspaceEntitlements,
  WorkspaceEntitlements,
} from '@/api/entitlements.api';

/**
 * useWorkspaceEntitlements
 * 
 * Hook for fetching workspace entitlements via REST API.
 * 
 * @param workspaceId - The workspace ID
 * @returns Query result with entitlements data
 */
export function useWorkspaceEntitlements(workspaceId: string | undefined) {
  return useQuery({
    queryKey: ['workspace-entitlements', workspaceId],
    queryFn: () => {
      if (!workspaceId) {
        throw new Error('Workspace ID is required');
      }
      return fetchWorkspaceEntitlements(workspaceId);
    },
    enabled: !!workspaceId,
    staleTime: 60 * 1000, // 1 minute
    retry: 1,
  });
}

/**
 * useUpdateWorkspaceEntitlements
 * 
 * Hook for updating workspace entitlements via REST API.
 * 
 * @param workspaceId - The workspace ID
 * @returns Mutation object for updating entitlements
 */
export function useUpdateWorkspaceEntitlements(workspaceId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (modules: string[]) => {
      if (!workspaceId) {
        throw new Error('Workspace ID is required');
      }
      return updateWorkspaceEntitlements(workspaceId, modules);
    },
    onSuccess: () => {
      // Invalidate and refetch entitlements
      queryClient.invalidateQueries({
        queryKey: ['workspace-entitlements', workspaceId],
      });
      // Also invalidate related queries
      queryClient.invalidateQueries({
        queryKey: ['entitlements', 'workspace', workspaceId],
      });
    },
  });
}
